import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { usersService } from "@/services/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import * as OTPAuth from "otpauth";
import { Shield, Key, Copy, Check, X } from "lucide-react";

const Settings = () => {
  const { user, userData, logout, refreshUserData } = useAuth();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [mfaStep, setMfaStep] = useState(userData?.mfaEnabled ? "verify" : "setup");
  const [totpSecret, setTotpSecret] = useState("");
  const [totpUri, setTotpUri] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaCopied, setMfaCopied] = useState(false);

  useEffect(() => {
    if (userData) {
      setName(userData.name || "");
      if (userData.photoURL) {
        setPhotoPreview(userData.photoURL);
      }
    }
  }, [userData?.id]);

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const generateMfaSecret = async () => {
    const totp = new OTPAuth.TOTP({
      issuer: "Havana Group Ops",
      label: userData?.email || "user",
      algorithm: "SHA1",
      digits: 6,
      period: 30,
      secret: new OTPAuth.Secret()
    });
    
    const secret = totp.secret.base32;
    setTotpSecret(secret);
    setTotpUri(totp.toString());
    setMfaStep("verify");
  };

  const enableMfa = async () => {
    if (!verificationCode || !totpSecret) {
      toast.error("Please enter the 6-digit code");
      return;
    }
    
    setMfaLoading(true);
    
    const totp = new OTPAuth.TOTP({
      issuer: "Havana Group Ops",
      label: userData?.email || "user",
      algorithm: "SHA1",
      digits: 6,
      period: 30,
      secret: OTPAuth.Secret.fromBase32(totpSecret)
    });
    
    const isValid = totp.validate({ window: 1 }) === 0;
    
    if (!isValid) {
      toast.error("Invalid code. Please sync your authenticator and try again.");
      setMfaLoading(false);
      return;
    }
    
    try {
      await usersService.update(user.uid, {
        mfaEnabled: true,
        mfaSecret: totpSecret,
        updatedAt: new Date().toISOString()
      });
      await refreshUserData();
      toast.success("MFA enabled successfully");
      setMfaStep("verify");
    } catch (error) {
      toast.error("Failed to enable MFA");
    }
    
    setMfaLoading(false);
  };

  const disableMfa = async () => {
    try {
      await usersService.update(user.uid, {
        mfaEnabled: false,
        mfaSecret: null,
        updatedAt: new Date().toISOString()
      });
      await refreshUserData();
      toast.success("MFA disabled");
      setMfaStep("setup");
      setTotpSecret("");
      setTotpUri("");
      setVerificationCode("");
    } catch (error) {
      toast.error("Failed to disable MFA");
    }
  };

  const copyTotpUri = () => {
    navigator.clipboard.writeText(totpUri);
    setMfaCopied(true);
    setTimeout(() => setMfaCopied(false), 2000);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Photo must be less than 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    const userId = user?.uid || userData?.id;
    if (!userId) {
      toast.error("User not loaded yet");
      return;
    }
    setSaving(true);
    try {
      const updateData = { name, photoURL: photoPreview };
      await usersService.update(userId, updateData);
      await refreshUserData();
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-navy">Settings</h1>
        <p className="text-gray-500">Manage your account settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Profile Settings</CardTitle>
            <CardDescription>Update your personal information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-gradient-to-br from-action-blue to-purple-500 rounded-full flex items-center justify-center overflow-hidden">
                {photoPreview ? (
                  <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white font-bold text-2xl">
                    {getInitials(userData?.name)}
                  </span>
                )}
              </div>
              <div>
                <label className="cursor-pointer">
                  <span 
                    className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Change Photo
                  </span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
                <p className="text-xs text-gray-500 mt-2">JPG, PNG. Max 2MB</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-navy">Full Name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-navy">Email</label>
                <Input defaultValue={userData?.email || ""} disabled />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-navy">Role</label>
                <Input defaultValue={userData?.role || ""} disabled />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-navy">Department</label>
                <Input defaultValue={userData?.department || ""} disabled />
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Account Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Role</p>
                <Badge className="bg-blue-100 text-blue-700">
                  {userData?.role || "Team Member"}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Department</p>
                <p className="font-medium text-navy">{userData?.department || "General"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Permissions</p>
                <div className="flex flex-wrap gap-2">
                  {userData?.permissions?.map((perm) => (
                    <Badge key={perm} variant="outline">
                      {perm}
                    </Badge>
                  )) || <Badge variant="outline">view</Badge>}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Two-Factor Authentication
              </CardTitle>
              <CardDescription>
                Secure your account with Google Authenticator or compatible apps
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {userData?.mfaEnabled ? (
                <div className="text-center space-y-3">
                  <div className="bg-green-50 text-green-700 px-4 py-2 rounded-lg inline-flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    MFA Enabled
                  </div>
                  <p className="text-sm text-gray-500">
                    Your account is protected with 2FA
                  </p>
                  <Button 
                    variant="outline" 
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    onClick={disableMfa}
                  >
                    Disable MFA
                  </Button>
                </div>
              ) : mfaStep === "setup" ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500">
                    Scan this QR code with your authenticator app, then enter the code to verify.
                  </p>
                  <Button onClick={generateMfaSecret} className="w-full">
                    <Key className="w-4 h-4 mr-2" />
                    Generate QR Code
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {totpUri && (
                    <div className="bg-white border rounded-lg p-4 flex justify-center">
                      <img 
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpUri)}`}
                        alt="Scan this QR code"
                        className="w-48 h-48"
                      />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={copyTotpUri}
                      title="Copy URI"
                    >
                      {mfaCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                    <Input
                      placeholder="Enter 6-digit code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="flex-1"
                      maxLength={6}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => { setMfaStep("setup"); setTotpSecret(""); setTotpUri(""); }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={enableMfa} disabled={mfaLoading || verificationCode.length !== 6} className="flex-1">
                      {mfaLoading ? "Verifying..." : "Enable MFA"}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Danger Zone</CardTitle>
              <CardDescription>Irreversible actions</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full border-red-200 text-red-600 hover:bg-red-50" onClick={logout}>
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Settings;
